import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import GoBack from "../components/GoBack";
import {
  getChangeRequest,
  payChangeRequest,
  PayTermChangeResult,
  TermChangeRequest,
} from "../api/services";
import { PaymentMethodPicker } from "../components/payment";
import { usePaymentMethods } from "../hooks/usePaymentMethods";
import { formatDate as formatDateOnly } from "@ui/shared";
import { formatCurrency } from "../utils/formatCurrency";

const usd = formatCurrency;

const formatDate = (s: string | null | undefined): string =>
  s
    ? formatDateOnly(s, { year: "numeric", month: "numeric", day: "numeric" })
    : "—";

/**
 * Pay the difference for a change to the active term. Same payment
 * mechanics as the renewal page (saved methods, ACH authorization for bank
 * accounts); the term isn't starting in the future, so any method type is
 * accepted.
 */
const PolicyChangePayment: React.FC = () => {
  const { requestUuid } = useParams<{ requestUuid: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const [request, setRequest] = useState<TermChangeRequest | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const { methods, loading: methodsLoading } = usePaymentMethods();
  const [selectedMethodId, setSelectedMethodId] = useState<number | null>(null);
  const [paying, setPaying] = useState(false);
  const [result, setResult] = useState<PayTermChangeResult | null>(null);
  const [mandateAccepted, setMandateAccepted] = useState(false);
  useEffect(() => {
    setMandateAccepted(false);
  }, [selectedMethodId]);

  useEffect(() => {
    if (!requestUuid) return;
    let cancelled = false;
    setLoading(true);
    getChangeRequest(requestUuid)
      .then((res) => {
        if (!cancelled) setRequest(res.data);
      })
      .catch(() => {
        if (!cancelled) setError(t("policyChange.notFound"));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [requestUuid, t]);

  useEffect(() => {
    if (selectedMethodId !== null || methods.length === 0) return;
    const def = methods.find((m) => m.isDefault) ?? methods[0];
    if (def) setSelectedMethodId(def.id);
  }, [methods, selectedMethodId]);

  if (loading) {
    return (
      <div>
        <GoBack to="/dashboard" />
        <p className="text-sm text-muted">{t("policyChange.loading")}</p>
      </div>
    );
  }

  if (!request) {
    return (
      <div>
        <GoBack to="/dashboard" />
        <div className="rounded-lg bg-red-50 px-4 py-3 text-sm text-danger">
          {error || t("policyChange.notFound")}
        </div>
      </div>
    );
  }

  const amount = request.difference;
  const selectedMethod = methods.find((m) => m.id === selectedMethodId) ?? null;
  const isBankSelected = selectedMethod !== null && selectedMethod.type !== "card";

  const handlePay = async () => {
    if (!selectedMethodId) return;
    if (isBankSelected && !mandateAccepted) return;
    setPaying(true);
    setError(null);
    setResult(null);
    try {
      const res = await payChangeRequest(
        request.uuid,
        selectedMethodId,
        isBankSelected ? true : undefined,
      );
      setResult(res.data);
      if (res.data.status === "succeeded") {
        setTimeout(() => navigate("/dashboard"), 1500);
      }
    } catch (err: any) {
      setError(
        err?.response?.data?.message ||
          err?.message ||
          t("policyChange.errorDefault"),
      );
    } finally {
      setPaying(false);
    }
  };

  const done = (title: string, body: string, redirecting: boolean) => (
    <div>
      <GoBack to="/dashboard" />
      <div className="rounded-xl bg-white shadow-sm">
        <div className="p-10 text-center">
          <h3 className="text-lg font-semibold text-primary">{title}</h3>
          <p className="mt-2 text-sm text-gray-600">{body}</p>
          {redirecting ? (
            <p className="mt-1 text-xs text-muted">
              {t("policyChange.success.redirecting")}
            </p>
          ) : (
            <button
              type="button"
              className="mt-6 cursor-pointer rounded-lg bg-primary px-6 py-2.5 text-sm font-medium text-white transition-colors hover:bg-primary-light"
              onClick={() => navigate("/dashboard")}
            >
              {t("policyChange.back")}
            </button>
          )}
        </div>
      </div>
    </div>
  );

  if (result?.status === "succeeded" || request.status === "applied") {
    return done(
      t("policyChange.success.title"),
      t("policyChange.success.body", { amount: usd(result?.amount ?? amount) }),
      result?.status === "succeeded",
    );
  }
  if (result?.status === "processing" || request.status === "processing") {
    return done(
      t("policyChange.processingState.title"),
      t("policyChange.processingState.body"),
      false,
    );
  }
  if (request.status !== "pending_payment") {
    return done(t("policyChange.closed.title"), t("policyChange.closed.body"), false);
  }

  const changes: { label: string; from: string; to: string }[] = [];
  if (request.requested.coverageAmount !== request.previous.coverageAmount) {
    changes.push({
      label: t("policyChange.coverageAmount"),
      from: usd(request.previous.coverageAmount),
      to: usd(request.requested.coverageAmount),
    });
  }
  if (request.requested.startsOn !== request.previous.startsOn) {
    changes.push({
      label: t("policyChange.startDate"),
      from: formatDate(request.previous.startsOn),
      to: formatDate(request.requested.startsOn),
    });
  }
  if (request.requested.endsOn !== request.previous.endsOn) {
    changes.push({
      label: t("policyChange.endDate"),
      from: formatDate(request.previous.endsOn),
      to: formatDate(request.requested.endsOn),
    });
  }

  const canPay =
    !!selectedMethodId &&
    !paying &&
    !methodsLoading &&
    methods.length > 0 &&
    (!isBankSelected || mandateAccepted);

  return (
    <div>
      <GoBack to="/dashboard" />

      {(error || result?.error) && (
        <div className="mb-4 rounded-lg bg-red-50 px-4 py-3 text-sm text-danger">
          {error || result?.error}
        </div>
      )}

      <div className="mb-6 rounded-xl bg-white shadow-sm">
        <div className="border-b border-gray-100 px-5 py-4">
          <h3 className="font-semibold text-primary">{t("policyChange.headerTitle")}</h3>
        </div>
        <div className="p-5">
          <p className="mb-4 text-sm text-gray-700">
            {t("policyChange.intro", { policy: request.policyName })}
          </p>
          <table className="mb-4 w-full text-sm">
            <tbody>
              {changes.map((c) => (
                <tr key={c.label} className="border-b border-gray-100">
                  <td className="py-2 font-medium text-gray-700">{c.label}</td>
                  <td className="py-2 text-muted">{c.from}</td>
                  <td className="py-2 text-right text-primary">→ {c.to}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="flex items-baseline justify-between">
            <small className="text-muted">{t("policyChange.amountSubtitle")}</small>
            <h4 className="text-primary">
              {t("policyChange.amountDue", { amount: usd(amount) })}
            </h4>
          </div>
        </div>
      </div>

      <div className="mb-6 rounded-xl bg-white shadow-sm">
        <div className="flex items-center justify-between border-b border-gray-100 px-5 py-4">
          <h5 className="font-semibold text-primary">{t("policyRenewal.chooseMethod")}</h5>
          <button
            type="button"
            className="cursor-pointer text-sm font-medium text-info underline hover:text-sky-600"
            onClick={() => navigate("/payment-methods")}
          >
            {t("policyRenewal.manageMethods")}
          </button>
        </div>
        <div className="p-5">
          {methodsLoading ? (
            <p className="text-sm text-muted">{t("policyRenewal.loadingMethods")}</p>
          ) : (
            <PaymentMethodPicker
              methods={methods}
              selectedId={selectedMethodId}
              onSelect={setSelectedMethodId}
              emptyMessage={t("policyRenewal.emptyMethods")}
            />
          )}
        </div>
      </div>

      {isBankSelected && (
        <label className="mb-6 flex cursor-pointer items-start gap-3 rounded-xl bg-white px-5 py-4 text-sm text-gray-700 shadow-sm">
          <input
            type="checkbox"
            className="mt-0.5 h-4 w-4 cursor-pointer"
            checked={mandateAccepted}
            onChange={(e) => setMandateAccepted(e.target.checked)}
            disabled={paying}
          />
          <span>{t("policyChange.achAuthorization", { amount: usd(amount) })}</span>
        </label>
      )}

      <div className="mb-6 flex flex-col items-end gap-2">
        <button
          className="cursor-pointer rounded-lg bg-primary px-8 py-3 text-base font-medium text-white transition-colors hover:bg-primary-light disabled:cursor-not-allowed disabled:opacity-50"
          disabled={!canPay}
          onClick={handlePay}
        >
          {paying
            ? t("policyRenewal.processing")
            : t("policyRenewal.payButton", { amount: usd(amount) })}
        </button>
        <small className="text-muted">
          {t("policyChange.payDisclaimer", { amount: usd(amount) })}
        </small>
      </div>
    </div>
  );
};

export default PolicyChangePayment;
